/*
 * Copyright © 2024 Himitsu Lab Limited. All Rights Reserved.
 */

/* eslint-disable react-hooks/exhaustive-deps */
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { useState, useRef, useEffect } from 'react';
import { Button } from '../Components';
import { Accordion, AccordionItem } from '../Components/base/accordion/accordion';
import Field from '../Components/base/field/field';
import Icon from '../Components/base/icon/icon';
import NewIcon from '../Components/base/icon/newIcons';
import { toastSuccess } from '../Components/toast';
import { useEventPolicyAgreementHooks } from '../Hooks/EventPolicyAgreementHooks';
import { EventCard } from './EventsCard/EventCard';
import beemg_arrow from '../Assets/icons/beemg-arrow.svg';
import { printContent } from '../Components/base/print-section/printContent';
import { markdownToHtmlWithHighlight } from '../Components/markdownToHtmlWithHighlight';

function EventPolicy() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { policies, eventPolicyAgreed, agree, filteredPolicies: activePolicyData } = useEventPolicyAgreementHooks()
  const [checkboxes, setCheckboxes] = useState<boolean[]>(
    activePolicyData ? new Array(activePolicyData.length).fill(false) : [],
  );
  const [isChecked, setIsChecked] = useState(false); // Track if all checkboxes are checked
  const [searchTerm, setSearchTerm] = useState('');

  const [searchResults, setSearchResults] = useState<any>([]);
  const policyRefs = useRef<HTMLDivElement[]>([]); // Refs for policy items

  const handleSearch = (term: string) => {
    const cleanedTerm = term.trim();
    setSearchTerm(cleanedTerm);

    if (!cleanedTerm) {
      setSearchResults([]);
      return;
    }

    setSearchResults({
      highlightedMainPolicyDescription: markdownToHtmlWithHighlight(policies?.description || "", cleanedTerm),
      updatedPolicies: activePolicyData?.map((policy) => ({
        ...policy,
        highlightedName: markdownToHtmlWithHighlight(policy.policyName || "", cleanedTerm),
        highlightedDescription: markdownToHtmlWithHighlight(policy.description || "", cleanedTerm),
      })) || [],
    });
  };

  const handleCheckboxChange = (index: number, checked: boolean) => {
    const newCheckboxes = [...checkboxes];
    newCheckboxes[index] = checked; // Update the specific checkbox
    setCheckboxes(newCheckboxes);
    // Check if all checkboxes are true
    const allChecked = newCheckboxes.every((checkbox) => checkbox === true);
    if (allChecked && newCheckboxes.length === activePolicyData?.length) {
      setIsChecked(allChecked); // Update the isChecked state
    } else {
      setIsChecked(false); // Reset the isChecked state
    }
  };
  
  const printAccordionContent = (index: number) => {
    printContent(`[data-content-index="${index}"]`, {
          title: 'BeeMG Policy',
          header: activePolicyData[index].policyName,
        });
  };

  const [subTermsContentVisible, setSubTermsContentVisible] = useState<boolean[]>([]);

  // Synchronize visibility state with policies data
  useEffect(() => {
    if (activePolicyData && activePolicyData.length > 0) {
      // Initialize all visibility states to false
      setSubTermsContentVisible(new Array(activePolicyData.length).fill(false));
    }
  }, [activePolicyData]);

  const togglePolicyDescription = (index: number) => {
    setSubTermsContentVisible(prev =>
      prev.map((visible, i) => (i === index ? !visible : visible)),
    );
  };

   useEffect(() => {
      if (eventPolicyAgreed) {
        toastSuccess(t('eventPolicyAgreed'))
        navigate('/s/events/newEvent')
      }
    }, [eventPolicyAgreed, navigate, t])

  return (
    <div className="flex flex-col h-screen">
      <div className="flex flex-col flex-1 bg-red-200">
        <div
          className={`fixed top-25 left-0 w-full z-20 px-2 py-0`}
        >
          <div
            className="fixed top-20 left-0 w-full z-20 px-2 py-0 overflow-auto bg-white"
          >
            {/* Title and Search Field */}
            <div className="flex flex-wrap items-center justify-between w-full mt-0">
              <div id="txt_eventPolicy" className="text-xl md:text-base font-semibold flex-grow text-center ml-0 md:ml-60">
                {t('eventCreationAgreement')}
              </div>
              <div className="w-full md:w-64 mt-4 md:mt-1 mr-0">
                <Field
                  name="policyName"
                  type="text"
                  data-testid="searchByName"
                  placeholder={t("searchByName")}
                  icon={<NewIcon icon="SEARCH_GLASS" stroke="#535353" />}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    handleSearch(e.target.value)
                  }
                />
              </div>
            </div>

            {/* Last Updated Section */}
            <div className="flex flex-col items-center justify-center space-y-2 px-4 py-2 min-h-[50px]">
              <p id="txt_lastUpdated" className="text-base whitespace-nowrap">
                {t("lastUpdatedEffectiveDate")}
                {": "}
                {policies?.updatedAt && format(new Date(policies.updatedAt), "dd MMMM yyyy")}
              </p>
              <p className="text-base whitespace-nowrap">{t("version")}: {policies?.policyVersion}</p>
            </div>

            {/* Marketplace Agreement */}
            <div className="text-center font-semibold text-sm mt-1 px-4 whitespace-nowrap">
              {t("theSiteIsAMarketplaceForServicesAndByUsingItYouAgreeToTheseTermsAndConditions")}
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div
        className={`flex-grow flex-1 overflow-y-auto mt-20 overflow-x-hidden min-h-[85vh] list-inherited`}
      >
        <div className="w-full flex flex-col items-center overflow-y-auto overflow-x-hidden justify-center">
          <div className="mb-8 gap-2 w-full lg:w-9/12 flex flex-col place-items-center">
            <div className="w-full mt-14 items-center">
              <div className="flex flex-col">
            {/* Accordion Section */}
                <div className="mt-6 mx-[5%] lg:mx-[10%] h-auto">
                  {activePolicyData &&
                    Array.from({ length: activePolicyData.length }).map((_, index) => (
                      <Accordion key={"acc" + index}>
                        <div
                          ref={(el) => {
                            if (el) {
                              policyRefs.current[index] = el;
                            }
                          }}
                          data-testid={`policyCard-${index}`}
                          id={`policyCard-${index}`}
                          className={`border border-gray-200 rounded-2xl w-full mb-2`}
                        >
                          <AccordionItem toggle={"accint" + index} color="card">
                            <EventCard keyId={index}>
                              <EventCard.Image>
                                <input
                                  type="checkbox"
                                  id="acceptTerms"
                                  className='rounded-md focus:outline-none focus:ring-2 focus:ring-white'
                                  name="acceptTerms"
                                  checked={checkboxes[index]}
                                  onChange={(e) =>
                                    handleCheckboxChange(index, e.target.checked)
                                  }
                                />
                              </EventCard.Image>
                              <EventCard.Title>
                                <span
                                  data-testid={`txt_title${index}`}
                                  id={`txt_title${index}`}
                                  className="text-base truncate mt-1"
                                  dangerouslySetInnerHTML={{
                                    __html:
                                      searchResults.updatedPolicies?.[index]?.highlightedName ??
                                      markdownToHtmlWithHighlight(activePolicyData?.[index]?.policyName ?? "", searchTerm),
                                  }}
                                />
                              </EventCard.Title>

                              <EventCard.Actions>
                                <button
                                  onClick={() => togglePolicyDescription(index)}
                                  className="p-2 rounded focus:outline-none hover:bg-gray-200"
                                >
                                  <img src={beemg_arrow} alt="Beemg Logo" width={15} height={15} className={`transition-transform duration-300 ${subTermsContentVisible[index]
                                    ? "rotate-90"
                                    : "rotate-0"
                                    }`} />
                                </button>
                              </EventCard.Actions>
                            </EventCard>
                          </AccordionItem>
                          {subTermsContentVisible[index] && (
                            <div id="policy-content-scroll" className="relative max-h-[450px] overflow-y-auto overflow-x-hidden p-4 text-sm">
                              {/* Print Icon for Accordion Content */}
                              <button
                                className="absolute mx-[3%] text-right top-2 right-2 focus:outline-none hover:bg-gray-200"
                                onClick={() => printAccordionContent(index)}
                              >
                                <Icon icon="PRINT" size="small" stroke="#535353" />
                              </button>

                              {/* Accordion Content */}
                              <div
                                data-content-index={index}
                                className="p-4"
                              >
                                <span
                                  dangerouslySetInnerHTML={{
                                    __html:
                                      searchResults.updatedPolicies?.[index]?.highlightedDescription ||
                                      markdownToHtmlWithHighlight(activePolicyData[index].description, searchTerm),
                                  }}
                                ></span>
                              </div>
                            </div>
                          )}
                        </div>
                      </Accordion>
                    ))}
                  {/* Buttons Section */}
                  <div className="flex">
                    <span className="w-full flex-wrap flex gap-2 mt-3 justify-end">
                        <>
                          <Button
                            data-testid="btn_accept"
                            id="btn_acceptTerms"
                            className="w-full md:w-64 normal-case"
                            disabled={!isChecked}
                            onClick={() => agree()}
                          >
                            {t("acceptAndContinue")}
                          </Button>
                        </>
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default EventPolicy;